/**
 * Sign In Modal Component
 *
 * Modern authentication modal with email/password sign-in and sign-up.
 * Uses hash-based navigation for better Android back button support.
 * Built with native HTML <dialog> element for better accessibility.
 *
 * @module ui/layout
 */

import { useState, useEffect, useRef } from "react";
import type { FormEvent } from "react";
import { CloseIcon, EyeIcon } from "@butter/ui/icons";
import { TenantLogo } from "@butter/ui/components/data-display";
import { useAuth } from "~/services/auth";
import { authService, WrongClubError, AccountSetupRequiredError, useAccountSetupRedirect } from "~/services/auth";
import { appRuntime } from "~/services/appRuntime";
import { buildClubEntryUrl } from "~/lib/queryParams";
import { isValidPhone, normalizePhone } from "@butter/library/phone";
import { useClubQuery } from "~/services/internalApi/club/hooks";
import { getPasswordStrength, validateUserPassword } from "~/lib/utils/passwordValidation";
import { useSignInPrompt, clearSignInPrompt } from './signInPrompt';

export interface SignInModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close modal callback — a USER-initiated close (X, ESC, backdrop). */
  onClose: () => void;
  /**
   * Close because WE are dismissing the modal to send the member somewhere else, not
   * because they asked to close it. Defaults to `onClose`.
   *
   * This exists because a call site's `onClose` may be history-based: `MainLayout` passes
   * `useHashModal.close`, which is `window.history.back()`. That is right for a user
   * close — it reverses the hash that opened the dialog — but wrong for a programmatic
   * one, where it walks the browser out of the app to whatever preceded it. `MainLayout`
   * already uses `forceClose` on the sign-in-success path for the same reason.
   */
  onDismiss?: () => void;
  /** Success callback */
  onSuccess: () => void;
  /** Pre-fill email from previous step */
  prefillEmail?: string;
}

/**
 * Sign In Modal Component
 *
 * Features:
 * - Clean, modern design inspired by best practices
 * - Email + Password authentication
 * - Forgot password flow
 * - Error handling with visual feedback
 * - Loading states
 * - Accessible form controls
 */
export function SignInModal({
  isOpen,
  onClose,
  onDismiss,
  onSuccess,
  prefillEmail = "",
}: SignInModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const { login, register } = useAuth();
  // The default region for a phone typed without a country code — the club being signed up at.
  const { data: club } = useClubQuery();
  const phoneRegion = club?.country ?? "US";
  const redirectToAccountSetup = useAccountSetupRedirect();

  // Mode toggle: sign-in vs sign-up
  type AuthMode = 'signIn' | 'signUp';
  const [mode, setMode] = useState<AuthMode>('signIn');

  // One-shot contextual prompt (message + email) from a flow that opened this
  // modal via the hash — e.g. the demo flow's "account already exists" handoff.
  const prompt = useSignInPrompt();

  // Initialize email: prefer prefillEmail, fallback to dev credentials
  const getInitialEmail = () => {
    if (prefillEmail) return prefillEmail;
    return import.meta.env.VITE_ENVIRONMENT !== "production" ? "teamlead.rangecollectivenorth@example.com" : "";
  };

  const [email, setEmail] = useState(getInitialEmail());
  const [password, setPassword] = useState(import.meta.env.VITE_ENVIRONMENT !== "production" ? "Password123!" : "");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the credentials are valid but the account lives at another club;
  // drives the "go to the right portal" redirect link below the error.
  const [wrongClub, setWrongClub] = useState<{ name?: string; handle?: string; clubId?: number } | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Visual strength meter (sign-up only, shared) — the pass/fail gate is validateUserPassword on submit.
  const passwordStrength = getPasswordStrength(password);

  // Sync email with prefillEmail prop when it changes (e.g., modal reopens with new email)
  useEffect(() => {
    if (prefillEmail) {
      setEmail(prefillEmail);
    }
  }, [prefillEmail]);

  // A prompt's email wins over the dev-credential default — the member just
  // typed it in the flow that sent them here. Render-phase adjust (the
  // you-might-not-need-an-effect pattern), keyed on the prompt object so it
  // seeds exactly once per handoff.
  const [seededPrompt, setSeededPrompt] = useState<typeof prompt>(null);
  // Only the OPEN instance seeds/renders the prompt — four SignInModal mounts
  // share the store (MainLayout, MobileMenu, two step-local), and a closed
  // dialog must not adopt the context or duplicate the banner in the DOM.
  if (isOpen && prompt?.email && prompt !== seededPrompt) {
    setSeededPrompt(prompt);
    setEmail(prompt.email);
    setPassword('');
  }

  // Sync dialog open/close with isOpen prop
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      dialog.showModal();
    } else {
      dialog.close();
      // The prompt is one-shot — a later plain open carries no stale context.
      clearSignInPrompt();
    }
  }, [isOpen]);

  const resetForm = () => {
    setEmail(prefillEmail);
    setPassword("");
    setConfirmPassword("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setError(null);
    setWrongClub(null);
    setShowForgotPassword(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setMode('signIn');
  };

  // User-initiated close (X, ESC, backdrop).
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Programmatic dismissal — we are navigating them elsewhere. Never history-based.
  const handleDismiss = () => {
    resetForm();
    (onDismiss ?? onClose)();
  };

  // Handle native dialog close events (ESC key, backdrop click)
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault(); // Prevent default ESC behavior
      handleClose();
    };

    const handleNativeClose = () => {
      // Dialog was closed (ESC or close())
      if (isOpen) {
        handleClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('close', handleNativeClose);

    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('close', handleNativeClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Handle sign in
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setWrongClub(null);

    // Shared validation
    if (!email || !password) {
      setError("Please enter your email and password");
      return;
    }

    // Sign-up specific validation
    if (mode === 'signUp') {
      if (!firstName.trim() || !lastName.trim()) {
        setError("Please enter your first and last name");
        return;
      }
      if (!phone.trim()) {
        setError("Please enter your phone number");
        return;
      }
      // Was a "contains at least one digit" regex, which accepted things like "1" and then
      // posted them raw — the widest gap between a client rule and the API's. `register` now
      // normalizes to E.164 or 400s, so match it here and keep the error inline.
      if (!isValidPhone(phone, phoneRegion)) {
        setError("Please enter a valid phone number");
        return;
      }
      const pwResult = validateUserPassword(password);
      if (!pwResult.isValid) {
        setError(pwResult.errors[0]);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match");
        return;
      }
    }

    try {
      setIsLoading(true);

      if (mode === 'signUp') {
        // Use auth context register (handles token storage automatically)
        // Validated above, so the fallback is unreachable; it exists because the result is a union.
        const normalizedPhone = normalizePhone(phone, phoneRegion);
        await register({
          email: email.toLowerCase().trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: normalizedPhone.ok ? normalizedPhone.e164 : "",
          clubId: Number(appRuntime.clubId),
        });
      } else {
        // Use auth context login (handles token storage automatically)
        await login(email.toLowerCase().trim(), password);
      }

      // Success - call parent callback
      onSuccess();
      handleClose();
    } catch (err) {
      console.error(`[SignInModal] ${mode === 'signUp' ? 'Registration' : 'Login'} failed:`, err);

      // The account exists but was never finished (`pending` / `incomplete`). These
      // members can NEVER authenticate — their password is a server-generated
      // placeholder — so "check your credentials" is advice they cannot act on. Send
      // them to the setup screen instead.
      //
      // Close the modal FIRST, then show the countdown. This is a native <dialog>
      // opened with showModal(), so it lives in the browser's top layer and paints over
      // the toast — leaving it open buries the only thing telling the member what is
      // happening. Closing also means the "Stay here" escape returns them to the page
      // they came from rather than to a sign-in form they cannot complete.
      if (err instanceof AccountSetupRequiredError) {
        handleDismiss();
        redirectToAccountSetup(err);
        return;
      }

      // Credentials are valid but the account belongs to another club — offer a
      // redirect to that club's portal instead of a dead-end error.
      if (err instanceof WrongClubError) {
        setWrongClub({ name: err.tenantName, handle: err.tenantHandle, clubId: err.clubId });
      }

      // Mode-1 re-auth: the email already has an account at
      // another club. Clear the typed password and re-prompt — resubmitting the
      // same form with their EXISTING password links the account here.
      if (mode === 'signUp' && (err as { code?: string } | null)?.code === 'EXISTING_ACCOUNT_PASSWORD_REQUIRED') {
        setPassword('');
        setConfirmPassword('');
        setError(err instanceof Error ? err.message : 'You have an account at another club — enter your current password.');
        return;
      }

      // Extract error message from the error object
      const errorMessage = err instanceof Error
        ? err.message
        : mode === 'signUp'
          ? "Registration failed. Please try again."
          : "Sign in failed. Please check your credentials and try again.";

      // Show error inline
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Switch between sign-in and sign-up modes
  const handleModeSwitch = () => {
    setError(null);
    setShowForgotPassword(false);
    // Clear only sensitive fields when switching modes
    // Keep email, name, and phone for convenience (user might switch back)
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setMode(mode === 'signIn' ? 'signUp' : 'signIn');
  };

  // Handle forgot password
  const handleForgotPassword = async () => {
    if (!email) {
      setError("Please enter your email address first");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      await authService.requestPasswordReset(email.toLowerCase().trim());

      setShowForgotPassword(true);
    } catch (err) {
      console.error("[SignInModal] Password reset failed:", err);

      const errorMessage = err instanceof Error
        ? err.message
        : "Failed to send password reset email. Please try again.";

      // Show error inline
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black/50 backdrop:backdrop-blur-sm bg-transparent p-4 m-auto max-w-md w-full rounded-2xl open:animate-in open:fade-in open:zoom-in-95 open:duration-200"
      onClick={(e) => {
        // Close when clicking backdrop (outside dialog content)
        const dialog = dialogRef.current;
        if (dialog && e.target === dialog) {
          handleClose();
        }
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full overflow-hidden">
        {/* Controls */}
        <div className="p-8 pb-6">
          <div className="flex flex-row-reverse justify-between">
            <button
              onClick={handleClose}
              className="mb-4 text-gray-400 text-interactive:text-gray-600 transition-colors rounded-lg p-1 hover:bg-gray-100"
              aria-label="Close modal"
            >
              <CloseIcon className="size-5" />
            </button>

            <div className="flex items-center gap-3 mb-2">
              <TenantLogo size="sm" />
            </div>
          </div>

          {/* Header */}
          <div className="mt-4">
            <h2
              id="signin-modal-title"
              className="text-2xl font-semibold text-gray-900 mb-2"
            >
              {showForgotPassword
                ? "Check your email"
                : mode === 'signUp'
                  ? "Create your account"
                  : "Sign in or register"}
            </h2>
            <p className="text-sm text-gray-600">
              {showForgotPassword
                ? `We sent a password reset link to ${email}`
                : mode === 'signUp'
                  ? "Enter your details to get started."
                  : "Ready when you are - access your bookings and continue."}
            </p>
            {prompt && isOpen && !showForgotPassword && (
              <p
                className="mt-2 rounded-lg border border-theme-200 bg-theme-50 px-3 py-2 text-sm text-theme-800"
                role="status"
                data-testid="signin-context-message"
              >
                {prompt.message}
              </p>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="px-8 pb-8">
          {showForgotPassword ? (
            // Password reset success message
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                <p className="text-sm text-green-800">
                  Please check your email and follow the instructions to reset
                  your password.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3 bg-theme-600 text-white rounded-xl font-medium hover:bg-theme-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            // Auth form (sign-in or sign-up)
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message - Always in DOM for accessibility */}
              <div
                className={`p-4 bg-red-50 border border-red-200 rounded-xl transition-all duration-200 ${
                  error ? 'opacity-100 visible' : 'opacity-0 invisible h-0 p-0 border-0'
                }`}
                aria-live="polite"
                aria-atomic="true"
                data-testid="signin-error-message"
              >
                <p className="text-sm text-red-800">{error || '\u00A0'}</p>
                {wrongClub?.handle && (
                  <a
                    href={buildClubEntryUrl({ tenantHandle: wrongClub.handle, clubId: wrongClub.clubId })}
                    data-testid="signin-wrong-club-link"
                    className="mt-2 inline-block text-sm font-medium text-theme-700 underline hover:text-theme-900"
                  >
                    Go to {wrongClub.name ?? 'your club'}
                  </a>
                )}
              </div>

              {/* Name Fields - Sign-up only */}
              {mode === 'signUp' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      htmlFor="signup-first-name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      First name <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="signup-first-name"
                      data-testid="signup-first-name-input"
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      disabled={isLoading}
                      placeholder="John"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      required
                      autoComplete="given-name"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="signup-last-name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Last name <span className="text-red-500" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="signup-last-name"
                      data-testid="signup-last-name-input"
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      disabled={isLoading}
                      placeholder="Doe"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label
                  htmlFor="signin-email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Email {mode === 'signUp' && <span className="text-red-500" aria-hidden="true">*</span>}
                </label>
                <input
                  id="signin-email"
                  data-testid="signin-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading}
                  placeholder="you@example.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                  required
                  autoComplete="email"
                />
              </div>

              {/* Phone Field - Sign-up only */}
              {mode === 'signUp' && (
                <div>
                  <label
                    htmlFor="signup-phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone number <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="signup-phone"
                    data-testid="signup-phone-input"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    disabled={isLoading}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    required
                    autoComplete="tel"
                  />
                </div>
              )}

              {/* Password Field */}
              <div>
                <label
                  htmlFor="signin-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password {mode === 'signUp' && <span className="text-red-500" aria-hidden="true">*</span>}
                </label>
                <div className="relative">
                  <input
                    id="signin-password"
                    data-testid="signin-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    placeholder={mode === 'signUp' ? "Min. 8 characters" : "Enter your password"}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    required
                    minLength={mode === 'signUp' ? 8 : undefined}
                    autoComplete={mode === 'signUp' ? "new-password" : "current-password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors p-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    aria-controls="signin-password"
                  >
                    <EyeIcon
                      isOpen={showPassword}
                      className="h-5 w-5"
                    />
                  </button>
                </div>

                {/* Password Strength Indicator - Sign-up only */}
                {mode === 'signUp' && password && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      <div className={`h-1 flex-1 rounded ${
                        passwordStrength.strength === 'weak' ? 'bg-red-500' :
                        passwordStrength.strength === 'medium' ? 'bg-yellow-500' :
                        'bg-green-500'
                      }`} />
                      <div className={`h-1 flex-1 rounded ${
                        passwordStrength.strength === 'medium' || passwordStrength.strength === 'strong'
                          ? passwordStrength.strength === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                          : 'bg-gray-200'
                      }`} />
                      <div className={`h-1 flex-1 rounded ${
                        passwordStrength.strength === 'strong' ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    </div>
                    <p className={`mt-1 text-xs ${
                      passwordStrength.strength === 'weak' ? 'text-red-600' :
                      passwordStrength.strength === 'medium' ? 'text-yellow-600' :
                      'text-green-600'
                    }`}>
                      {passwordStrength.message}
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password Field - Sign-up only */}
              {mode === 'signUp' && (
                <div>
                  <label
                    htmlFor="signup-confirm-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm password <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="signup-confirm-password"
                      data-testid="signup-confirm-password-input"
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={isLoading}
                      placeholder="Re-enter password"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-theme-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={isLoading}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 disabled:opacity-50 transition-colors p-1"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      aria-pressed={showConfirmPassword}
                      aria-controls="signup-confirm-password"
                    >
                      <EyeIcon
                        isOpen={showConfirmPassword}
                        className="h-5 w-5"
                      />
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">
                      Passwords do not match
                    </p>
                  )}
                </div>
              )}

              {/* Continue Button */}
              <button
                type="submit"
                data-testid="signin-submit-btn"
                disabled={isLoading}
                className={`
                  w-full px-6 py-3 rounded-xl font-medium
                  transition-all duration-200
                  ${
                    isLoading
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-theme-600 text-white hover:bg-theme-700 active:scale-[0.98]"
                  }
                `}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    {mode === 'signUp' ? 'Creating account...' : 'Signing in...'}
                  </span>
                ) : (
                  mode === 'signUp' ? "Create account" : "Continue"
                )}
              </button>

              {/* Forgot Password Link - Sign-in only */}
              {mode === 'signIn' && (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    disabled={isLoading}
                    className="text-sm text-gray-600 hover:text-gray-900 font-medium disabled:opacity-50 transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>
              )}

              {/* Mode Toggle */}
              <div className="text-center pt-1">
                <p className="text-sm text-gray-600">
                  {mode === 'signIn' ? "Don't have an account? " : "Already have an account? "}
                  <button
                    type="button"
                    data-testid="auth-mode-toggle-btn"
                    onClick={handleModeSwitch}
                    disabled={isLoading}
                    className="text-theme-600 hover:text-theme-700 font-medium disabled:opacity-50 transition-colors"
                  >
                    {mode === 'signIn' ? 'Sign up' : 'Sign in'}
                  </button>
                </p>
              </div>

              {/* Terms */}
              <p className="text-xs text-center text-gray-500 pt-2">
                By continuing, you agree to our{" "}
                <a
                  href="https://buttersoftware.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-theme-600 hover:text-theme-700 underline"
                >
                  Terms and Conditions
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </dialog>
  );
}
